import type { FieldSchema, Filter } from '@/stores'
import { applyFilters } from '@/stores'
import { numberToCurrency } from '@/utils'
import type { RealizedRow } from '../types'

type Term = 'short' | 'long'

export type TermKey = 'all' | Term

export const termOf = (row: Pick<RealizedRow, 'term'>): Term | null => {
  const term = (row.term ?? '').toLowerCase()
  if (term.startsWith('short')) return 'short'
  if (term.startsWith('long')) return 'long'
  return null
}

export const realizedFilterSchema = (
  accountNames: Map<string, string> | null,
): FieldSchema[] => [
  ...(accountNames
    ? [
        {
          field: 'account',
          label: 'Account',
          type: 'select' as const,
          multiple: true,
          formatter: (id: string) => accountNames.get(id) ?? id,
        },
      ]
    : []),
  { field: 'liquidationDate', label: 'Sale date', type: 'date' },
  { field: 'description', label: 'Description', type: 'text' },
  {
    field: 'gainLoss',
    label: 'Gain or loss',
    type: 'number',
    formatter: (value: number) => numberToCurrency(value),
  },
]

const matchesSearch = (row: RealizedRow, search: string) => {
  const needle = search.trim().toLowerCase()
  return (
    !needle ||
    row.description.toLowerCase().includes(needle) ||
    (row.symbol ?? '').toLowerCase().includes(needle)
  )
}

export interface RealizedView {
  rows: RealizedRow[]
  termCounts: Record<TermKey, number>
}

// Filters and search first, so the term counts add up to the rows; then the chosen term.
const buildRealizedView = (
  rows: RealizedRow[],
  schema: FieldSchema[],
  filters: Filter[],
  search: string,
  term: TermKey,
): RealizedView => {
  const filtered = (
    applyFilters(rows, filters, schema) as RealizedRow[]
  ).filter((row) => matchesSearch(row, search))
  const termCounts = { all: filtered.length, short: 0, long: 0 }
  for (const row of filtered) {
    const key = termOf(row)
    if (key) termCounts[key] += 1
  }
  return {
    rows:
      term === 'all'
        ? filtered
        : filtered.filter((row) => termOf(row) === term),
    termCounts,
  }
}

const VIEWS_KEPT_PER_LIST = 8

const viewsByRows = new WeakMap<RealizedRow[], Map<string, RealizedView>>()

// Same rows and same filters give back the same view, so a re-render does no work.
export const realizedView = (
  rows: RealizedRow[],
  schema: FieldSchema[],
  filters: Filter[],
  search: string,
  term: TermKey,
): RealizedView => {
  const views = viewsByRows.get(rows) ?? new Map<string, RealizedView>()
  viewsByRows.set(rows, views)
  const key = JSON.stringify({ filters, search, term })
  const cached = views.get(key)
  if (cached) return cached
  if (views.size >= VIEWS_KEPT_PER_LIST) views.clear()
  const view = buildRealizedView(rows, schema, filters, search, term)
  views.set(key, view)
  return view
}

export interface RealizedTotals {
  // Null when no row printed the figure.
  proceeds: number | null
  costBasis: number | null
  shortTerm: { amount: number; count: number }
  longTerm: { amount: number; count: number }
  net: number
  saleCount: number
}

const cents = (amount: number | null | undefined) =>
  Math.round((amount ?? 0) * 100)

const totalsByRows = new WeakMap<RealizedRow[], RealizedTotals>()

// Added up from the realized gain rows the list shows. Built once per list.
export const realizedTotals = (rows: RealizedRow[]): RealizedTotals => {
  const cached = totalsByRows.get(rows)
  if (cached) return cached
  const totals = sumRealized(rows)
  totalsByRows.set(rows, totals)
  return totals
}

const sumRealized = (rows: RealizedRow[]): RealizedTotals => {
  const sums = { proceeds: 0, costBasis: 0, short: 0, long: 0, net: 0 }
  const counts = { short: 0, long: 0 }
  for (const row of rows) {
    sums.proceeds += cents(row.saleAmount)
    sums.costBasis += cents(row.costBasis)
    sums.net += cents(row.gainLoss)
    const key = termOf(row)
    if (key) {
      sums[key] += cents(row.gainLoss)
      counts[key] += 1
    }
  }
  const printed = (pick: (row: RealizedRow) => number | null) =>
    rows.some((row) => pick(row) != null)
  return {
    proceeds: printed((row) => row.saleAmount) ? sums.proceeds / 100 : null,
    costBasis: printed((row) => row.costBasis)
      ? (0 - sums.costBasis) / 100 || 0
      : null,
    shortTerm: { amount: sums.short / 100, count: counts.short },
    longTerm: { amount: sums.long / 100, count: counts.long },
    net: sums.net / 100,
    saleCount: rows.length,
  }
}

const EXPORT_COLUMNS = [
  { key: 'liquidationDate', label: 'Sale date' },
  { key: 'accountLabel', label: 'Account' },
  { key: 'term', label: 'Term' },
  { key: 'description', label: 'Description' },
  { key: 'symbol', label: 'Symbol' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'saleAmount', label: 'Proceeds' },
  { key: 'costBasis', label: 'Cost basis' },
  { key: 'gainLoss', label: 'Gain or loss' },
  { key: 'statementFileName', label: 'Statement file' },
  { key: 'sourcePage', label: 'Page' },
]

export const realizedExport = (rows: RealizedRow[]) => ({
  columns: EXPORT_COLUMNS,
  rows: rows as unknown as Record<string, unknown>[],
})
